import { useEffect } from 'react'
import { useLocation, useHistory } from 'react-router-dom'

export function useSyncParamsWithRoute(
  params: any,
  setParams: React.Dispatch<React.SetStateAction<any>>,
) {
  const location = useLocation()
  const history = useHistory()

  const arraysShallowEqual = (
    a?: string[] | undefined,
    b?: string[] | undefined,
  ) => {
    if (a === b) return true
    if (!a && !b) return true
    if (!a || !b) return false
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false
    }
    return true
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const page = parseInt(searchParams.get('page') || '') || undefined
    const limit = parseInt(searchParams.get('limit') || '') || undefined
    const tab = searchParams.get('tab') || undefined
    const status = searchParams.getAll('status').length > 0
      ? searchParams?.getAll('status') : undefined
    const queryString = searchParams.get('queryString') || undefined
    const timeStatus = searchParams.getAll('timeStatus').length > 0
      ? searchParams.getAll('timeStatus')
      : undefined
    const categoryIds =
      searchParams.getAll('categoryIds').length > 0
        ? searchParams.getAll('categoryIds')
        : undefined
    const eventType = searchParams.getAll('eventType').length > 0
      ? searchParams.getAll('eventType')
      : undefined
    const distance = searchParams.get('distance') || undefined
    const lat = searchParams.get('lat') || undefined
    const lng = searchParams.get('lng') || undefined
    const sortBy = searchParams.get('sortBy') || undefined
    const orderBy = searchParams.get('orderBy') || undefined
    const date = searchParams.get('date') || undefined
    setParams((prev: any) => {
      const next = {
        ...prev,
        page,
        limit,
        tab,
        status,
        queryString,
        timeStatus,
        categoryIds,
        eventType,
        distance: distance ? parseInt(distance) : undefined,
        lat: lat ? parseFloat(lat) : undefined,
        lng: lng ? parseFloat(lng) : undefined,
        sortBy,
        orderBy,
        date,
      }

      const arraysEqual = arraysShallowEqual(
        prev?.categoryIds,
        next.categoryIds,
      )

      const unchanged =
        prev?.page === next.page &&
        prev?.limit === next.limit &&
        prev?.tab === next.tab &&
        prev?.status === next.status &&
        prev?.queryString === next.queryString &&
        prev?.timeStatus === next.timeStatus &&
        arraysEqual &&
        prev?.eventType === next.eventType &&
        prev?.distance === next.distance &&
        prev?.lat === next.lat &&
        prev?.lng === next.lng &&
        prev?.sortBy === next.sortBy &&
        prev?.orderBy === next.orderBy &&
        prev?.date === next.date

      return unchanged ? prev : next
    })
  }, [location.search, setParams])

  const updateRouteParams = (newParams: {
    page?: number
    limit?: number
    tab?: string
    status?: string[] | string | null
    queryString?: string
    timeStatus?: string[] | string | null
    categoryIds?: string[] | string | null
    eventType?: string[] | string | null
    distance?: number | null
    lat?: number | null
    lng?: number | null
    sortBy?: string | null
    orderBy?: string | null
    date?: string | null
  }) => {
    const searchParams = new URLSearchParams(location.search)

    if (newParams.page !== undefined) {
      searchParams.set('page', String(newParams.page))
    }

    if (newParams.limit !== undefined) {
      searchParams.set('limit', String(newParams.limit))
    }

    if (newParams.tab !== undefined) {
      searchParams.set('tab', String(newParams.tab))
    }

    if (newParams.status !== undefined) {
      searchParams.delete('status')
      if (Array.isArray(newParams.status)) {
        newParams.status.forEach(status =>
          searchParams.append('status', status),
        )
      }
    }

    if (newParams.queryString !== undefined) {
      searchParams.set('queryString', String(newParams.queryString))
    }

    if (newParams.timeStatus !== undefined) {
      searchParams.delete('timeStatus')

      if (Array.isArray(newParams.timeStatus)) {
        newParams.timeStatus.forEach(status =>
          searchParams.append('timeStatus', status),
        )
      }
    }
    
    if (newParams.categoryIds !== undefined) {
      if (
        newParams.categoryIds === null ||
        newParams.categoryIds === '' ||
        (Array.isArray(newParams.categoryIds) &&
          newParams.categoryIds.length === 0)
      ) {
        // Remove all categoryIds parameters
        searchParams.delete('categoryIds')
      } else {
        // Remove existing categoryIds first
        searchParams.delete('categoryIds')

        if (Array.isArray(newParams.categoryIds)) {
          // Add each categoryId as a separate parameter
          newParams.categoryIds.forEach(categoryId => {
            searchParams.append('categoryIds', categoryId)
          })
        } else {
          searchParams.set('categoryIds', newParams.categoryIds)
        }
      }
    }

    if (newParams.eventType !== undefined) {
      searchParams.delete('eventType')
      if (Array.isArray(newParams.eventType)) {
        newParams?.eventType.forEach(type => {
          searchParams?.append('eventType', type)
        })
      }
    }

    if (newParams.distance !== undefined) {
      if (newParams.distance === null) {
        searchParams.delete('distance')
      } else {
        searchParams.set('distance', String(newParams.distance))
      }
    }

    if (newParams.lat !== undefined) {
      if (newParams.lat === null) {
        searchParams.delete('lat')
      } else {
        searchParams.set('lat', String(newParams.lat))
      }
    }

    if (newParams.lng !== undefined) {
      if (newParams.lng === null) {
        searchParams.delete('lng')
      } else {
        searchParams.set('lng', String(newParams.lng))
      }
    }

    if (newParams.sortBy !== undefined) {
      if (newParams.sortBy === null || newParams.sortBy === '') {
        searchParams.delete('sortBy')
      } else {
        searchParams.set('sortBy', String(newParams.sortBy))
      }
    }

    if (newParams.orderBy !== undefined) {
      if (newParams.orderBy === null || newParams.orderBy === '') {
        searchParams.delete('orderBy')
      } else {
        searchParams.set('orderBy', String(newParams.orderBy))
      }
    }

    if (newParams.date !== undefined) {
      if (newParams.date === null || newParams.date === '') {
        searchParams.delete('date')
      } else {
        searchParams.set('date', String(newParams.date))
      }
    }

    history.replace({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
    })
  }

  return {
    updateRouteParams,
  }
}
